import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import {
  Loader2,
  Search,
  Download,
  MapPin,
  Building2,
  Star,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  FileText,
  Globe,
  Phone,
  Link2,
  Sparkles,
  Plus,
  X,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const AdminSEOImport = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [keywords, setKeywords] = useState(["homestay", "guest house", "hotel", "resort", "villa"]);
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [stopEnriching, setStopEnriching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [enrichResults, setEnrichResults] = useState(null);
  const [customLocation, setCustomLocation] = useState("");
  const [customLocations, setCustomLocations] = useState([]);

  useEffect(() => {
    fetchStatus();
    fetchLocations();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/dataforseo/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch status:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/dataforseo/locations`);
      setLocations(response.data.locations);
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    }
  };

  const toggleLocation = (location) => {
    setSelectedLocations((prev) =>
      prev.includes(location)
        ? prev.filter((l) => l !== location)
        : [...prev, location]
    );
  };

  const addCustomLocation = () => {
    if (!customLocation.trim()) {
      toast.error("Please enter a location");
      return;
    }
    
    // Format: "City, State, Country" or just "City, India"
    let formattedLocation = customLocation.trim();
    if (!formattedLocation.includes(",")) {
      formattedLocation = `${formattedLocation}, Maharashtra, India`;
    } else if (!formattedLocation.toLowerCase().includes("india")) {
      formattedLocation = `${formattedLocation}, India`;
    }
    
    if (customLocations.includes(formattedLocation) || locations.includes(formattedLocation)) {
      toast.error("Location already exists");
      return;
    }
    
    setCustomLocations(prev => [...prev, formattedLocation]);
    setCustomLocation("");
    toast.success(`Added: ${formattedLocation}`);
  };

  const removeCustomLocation = (location) => {
    setCustomLocations(prev => prev.filter(l => l !== location));
    setSelectedLocations(prev => prev.filter(l => l !== location));
  };

  const selectAllLocations = () => {
    setSelectedLocations([...locations, ...customLocations]);
  };

  const clearLocations = () => {
    setSelectedLocations([]);
  };

  const handleSearch = async () => {
    if (selectedLocations.length === 0) {
      toast.error("Please select at least one location");
      return;
    }

    setSearching(true);
    setSearchResults(null);

    try {
      // Search one location at a time for preview
      const location = selectedLocations[0];
      const response = await axios.post(
        `${API_URL}/dataforseo/search`,
        {
          location: location,
          keywords: keywords,
          limit: 50,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSearchResults(response.data);
      toast.success(`Found ${response.data.total_found} properties in ${location}`);
    } catch (error) {
      console.error("Search failed:", error);
      toast.error(error.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleImport = async () => {
    if (selectedLocations.length === 0) {
      toast.error("Please select at least one location");
      return;
    }

    const confirmMsg = selectedLocations.length === locations.length
      ? `Import from ALL ${locations.length} Kokan locations?`
      : `Import from ${selectedLocations.length} selected location(s)?`;

    if (!window.confirm(`${confirmMsg}\n\nThis will create new DRAFT listings that you can review and publish.`)) {
      return;
    }

    setImporting(true);
    setImportResults(null);

    try {
      const response = await axios.post(
        `${API_URL}/dataforseo/import`,
        {
          locations: selectedLocations,
          keywords: keywords,
          skip_duplicates: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setImportResults(response.data.results);
      toast.success(`Imported ${response.data.results.total_imported} listings as drafts`);
      fetchStatus(); // Refresh stats
    } catch (error) {
      console.error("Import failed:", error);
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleEnrich = async (autoContinue = false) => {
    // Check if user requested to stop
    if (autoContinue && stopEnriching) {
      setEnriching(false);
      setStopEnriching(false);
      toast.info("Enrichment stopped by user");
      return;
    }

    // Only show confirmation on first run (not auto-continue)
    if (!autoContinue) {
      if (!status?.pending_enrich || status?.pending_enrich === 0) {
        toast.info("All listings have already been enriched");
        return;
      }

      if (!window.confirm(`Enrich up to 50 listings with Google Places data?\n\nThis will add phone numbers, websites, and reviews (max 5) where available.\n\nRecords are processed in batches of 50 and marked as enriched to avoid reprocessing.`)) {
        return;
      }
      setStopEnriching(false);  // Reset stop flag on new run
    }

    setEnriching(true);
    if (!autoContinue) {
      setEnrichResults(null);
    }

    try {
      const response = await axios.post(
        `${API_URL}/dataforseo/enrich`,
        {
          limit: 50,
          only_missing: true,
          skip_enriched: true,  // Skip already enriched records
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const results = response.data.results;
      setEnrichResults(results);
      toast.success(`Enriched ${results.enriched} listings. ${results.remaining} remaining.`);
      fetchStatus(); // Refresh stats

      // Auto-continue if there are more records to process and user hasn't stopped
      if (results.remaining > 0 && results.enriched > 0 && !stopEnriching) {
        toast.info(`Auto-continuing with next batch (${results.remaining} remaining)...`);
        // Small delay before next batch to avoid overwhelming the API
        setTimeout(() => {
          handleEnrich(true);
        }, 2000);
      } else if (results.remaining === 0) {
        toast.success("All listings have been enriched!");
        setEnriching(false);
        setStopEnriching(false);
      } else {
        setEnriching(false);
        setStopEnriching(false);
      }
    } catch (error) {
      console.error("Enrich failed:", error);
      toast.error(error.response?.data?.detail || "Enrich failed");
      setEnriching(false);
      setStopEnriching(false);
    }
  };

  const handleStopEnrich = () => {
    setStopEnriching(true);
    toast.info("Stopping after current batch completes...");
  };

  const toggleKeyword = (keyword) => {
    setKeywords((prev) =>
      prev.includes(keyword)
        ? prev.filter((k) => k !== keyword)
        : [...prev, keyword]
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-seo-import">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1
              className="text-3xl font-semibold text-foreground mb-2"
             
            >
              DataForSEO Import
            </h1>
            <p className="text-muted-foreground">
              Bulk import hotel and homestay listings from DataForSEO Hotels API
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchStatus}
            className="rounded-full"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Status
          </Button>
        </div>

        {/* Status Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              API Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  {status?.configured ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  )}
                  <span className="text-sm font-medium">API Status</span>
                </div>
                <p className="text-lg font-bold">
                  {status?.configured ? "Configured" : "Not Configured"}
                </p>
                {status?.login && (
                  <p className="text-xs text-muted-foreground">{status.login}</p>
                )}
              </div>
              <div className="bg-amber-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-5 h-5 text-amber-500" />
                  <span className="text-sm font-medium">Draft Listings</span>
                </div>
                <p className="text-2xl font-bold text-amber-600">
                  {status?.draft_count || 0}
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium">Published</span>
                </div>
                <p className="text-2xl font-bold text-green-600">
                  {status?.published_count || 0}
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="w-5 h-5 text-blue-500" />
                  <span className="text-sm font-medium">Locations</span>
                </div>
                <p className="text-2xl font-bold text-blue-600">
                  {status?.locations_available || 0}
                </p>
              </div>
            </div>
            
            {/* Enrich with Google Places Section */}
            {status?.pending_enrich > 0 && (
              <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h4 className="font-semibold text-purple-800 mb-1 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Enrich with Google Places (Batch: 50 at a time)
                    </h4>
                    <p className="text-xs text-purple-600 mb-2">
                      Adds phone, website, and up to 5 reviews per listing. Auto-continues until complete.
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm text-purple-700">
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        {status?.enriched_count || 0} enriched
                      </span>
                      <span className="flex items-center gap-1">
                        <AlertCircle className="w-4 h-4 text-amber-500" />
                        {status?.pending_enrich || 0} pending
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {status?.missing_phone || 0} missing phone
                      </span>
                      <span className="flex items-center gap-1">
                        <Link2 className="w-4 h-4" />
                        {status?.missing_website || 0} missing website
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {enriching && (
                      <Button
                        onClick={handleStopEnrich}
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        data-testid="stop-enrich-btn"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Stop
                      </Button>
                    )}
                    <Button
                      onClick={() => handleEnrich(false)}
                      disabled={enriching}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="enrich-btn"
                    >
                      {enriching ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Enriching...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Start Enrichment
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* All Enriched Message */}
            {status?.pending_enrich === 0 && status?.enriched_count > 0 && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span className="font-semibold text-green-700">
                    All {status?.enriched_count} listings have been enriched!
                  </span>
                </div>
              </div>
            )}
            
            {/* Enrich Results */}
            {enrichResults && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg">
                <h4 className="font-semibold text-green-700 mb-2">
                  {enriching ? "Enrichment in Progress..." : "Enrich Results:"}
                </h4>
                
                {/* Progress Bar */}
                {enrichResults.remaining > 0 && (
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Progress</span>
                      <span>{Math.round((status?.enriched_count || 0) / ((status?.enriched_count || 0) + enrichResults.remaining) * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${Math.round((status?.enriched_count || 0) / ((status?.enriched_count || 0) + enrichResults.remaining) * 100)}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-sm">
                  <span>Processed: <strong>{enrichResults.total_processed}</strong></span>
                  <span>Enriched: <strong className="text-green-600">{enrichResults.enriched}</strong></span>
                  <span>Phone: <strong className="text-blue-600">+{enrichResults.phone_added}</strong></span>
                  <span>Website: <strong className="text-purple-600">+{enrichResults.website_added}</strong></span>
                  <span>Reviews: <strong className="text-amber-600">+{enrichResults.reviews_added || 0}</strong></span>
                  <span>Remaining: <strong className="text-gray-600">{enrichResults.remaining}</strong></span>
                </div>
                
                {enriching && enrichResults.remaining > 0 && (
                  <div className="mt-3 p-2 bg-purple-100 rounded text-sm text-purple-700 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Auto-continuing... Processing next batch in 2 seconds
                  </div>
                )}
                
                {!enriching && enrichResults.remaining > 0 && (
                  <div className="mt-3 flex gap-2">
                    <Button
                      onClick={() => handleEnrich(false)}
                      size="sm"
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Resume ({enrichResults.remaining} remaining)
                    </Button>
                  </div>
                )}
                
                {enrichResults.remaining === 0 && (
                  <div className="mt-3 p-2 bg-green-100 rounded text-sm text-green-700 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    All listings have been enriched!
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Location Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Select Locations
            </CardTitle>
            <CardDescription>
              Choose which locations to import from. You can also add custom locations.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Add Custom Location */}
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Add Custom Location</h4>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customLocation}
                  onChange={(e) => setCustomLocation(e.target.value)}
                  placeholder="e.g., Goa, Pune, Mumbai..."
                  className="flex-1 px-3 py-2 rounded-lg border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyDown={(e) => e.key === 'Enter' && addCustomLocation()}
                  data-testid="custom-location-input"
                />
                <Button
                  onClick={addCustomLocation}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="add-location-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
              <p className="text-xs text-blue-600 mt-1">Enter city name. "Maharashtra, India" will be added automatically.</p>
            </div>
            
            <div className="flex gap-2 mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={selectAllLocations}
              >
                Select All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={clearLocations}
              >
                Clear All
              </Button>
              <span className="ml-auto text-sm text-muted-foreground">
                {selectedLocations.length} of {locations.length + customLocations.length} selected
              </span>
            </div>
            
            {/* Custom Locations */}
            {customLocations.length > 0 && (
              <div className="mb-4">
                <h5 className="text-sm font-medium text-gray-600 mb-2">Custom Locations:</h5>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {customLocations.map((location) => (
                    <label
                      key={location}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedLocations.includes(location)
                          ? "border-blue-500 bg-blue-50"
                          : "border-blue-200 hover:border-blue-300"
                      }`}
                    >
                      <Checkbox
                        checked={selectedLocations.includes(location)}
                        onCheckedChange={() => toggleLocation(location)}
                      />
                      <span className="text-sm font-medium flex-1">
                        {location.split(",")[0]}
                      </span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          removeCustomLocation(location);
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            {/* Default Kokan Locations */}
            <h5 className="text-sm font-medium text-gray-600 mb-2">Kokan Region:</h5>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {locations.map((location) => (
                <label
                  key={location}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedLocations.includes(location)
                      ? "border-primary bg-primary/5"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <Checkbox
                    checked={selectedLocations.includes(location)}
                    onCheckedChange={() => toggleLocation(location)}
                  />
                  <span className="text-sm font-medium">
                    {location.split(",")[0]}
                  </span>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Property Types */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Property Types
            </CardTitle>
            <CardDescription>
              Select which types of properties to search for
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {["homestay", "guest house", "hotel", "resort", "villa"].map((keyword) => (
                <label
                  key={keyword}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border cursor-pointer transition-all ${
                    keywords.includes(keyword)
                      ? "border-primary bg-primary text-white"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <Checkbox
                    checked={keywords.includes(keyword)}
                    onCheckedChange={() => toggleKeyword(keyword)}
                    className={keywords.includes(keyword) ? "border-white data-[state=checked]:bg-white data-[state=checked]:text-primary" : ""}
                  />
                  <span className="text-sm font-medium capitalize">{keyword}</span>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <Button
            onClick={handleSearch}
            disabled={searching || selectedLocations.length === 0}
            variant="outline"
            className="flex-1"
            data-testid="preview-btn"
          >
            {searching ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                Preview First Location
              </>
            )}
          </Button>
          <Button
            onClick={handleImport}
            disabled={importing || selectedLocations.length === 0 || !status?.configured}
            className="flex-1 bg-green-600 hover:bg-green-700"
            data-testid="import-btn"
          >
            {importing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Import as Drafts
              </>
            )}
          </Button>
        </div>

        {/* Search Results Preview */}
        {searchResults && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="w-5 h-5" />
                Search Preview: {searchResults.location}
              </CardTitle>
              <CardDescription>
                Found {searchResults.total_found} properties. This is a preview of what will be imported.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {searchResults.hotels?.slice(0, 20).map((hotel, idx) => (
                  <div
                    key={hotel.id || idx}
                    className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg"
                  >
                    {hotel.images?.[0] && (
                      <img
                        src={hotel.images[0]}
                        alt={hotel.name}
                        className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-foreground truncate">
                        {hotel.name}
                      </h4>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        {hotel.rating > 0 && (
                          <span className="flex items-center gap-1">
                            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                            {hotel.rating}
                            {hotel.review_count > 0 && (
                              <span className="text-muted-foreground">
                                ({hotel.review_count} reviews)
                              </span>
                            )}
                          </span>
                        )}
                        {hotel.price_inr && (
                          <span className="text-green-600 font-medium">
                            ₹{hotel.price_inr}/night
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {searchResults.hotels?.length > 20 && (
                  <p className="text-center text-sm text-muted-foreground py-2">
                    ... and {searchResults.hotels.length - 20} more
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Import Results */}
        {importResults && (
          <Card className="border-green-200 bg-green-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="w-5 h-5" />
                Import Complete
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">
                    {importResults.total_searched}
                  </p>
                  <p className="text-sm text-muted-foreground">Locations Searched</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">
                    {importResults.total_found}
                  </p>
                  <p className="text-sm text-muted-foreground">Properties Found</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-green-600">
                    {importResults.total_imported}
                  </p>
                  <p className="text-sm text-muted-foreground">Imported as Drafts</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">
                    {importResults.duplicates_skipped}
                  </p>
                  <p className="text-sm text-muted-foreground">Duplicates Skipped</p>
                </div>
              </div>

              {importResults.errors?.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg">
                  <h4 className="font-semibold text-red-700 mb-2">Errors:</h4>
                  <ul className="text-sm text-red-600 space-y-1">
                    {importResults.errors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {importResults.imported_listings?.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-700 mb-2">
                    Recently Imported ({importResults.imported_listings.length}):
                  </h4>
                  <div className="max-h-[200px] overflow-y-auto space-y-2">
                    {importResults.imported_listings.slice(0, 10).map((listing, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-2 bg-white rounded"
                      >
                        <span className="font-medium">{listing.name}</span>
                        <span className="text-sm text-muted-foreground">
                          {listing.location}
                        </span>
                      </div>
                    ))}
                    {importResults.imported_listings.length > 10 && (
                      <p className="text-center text-sm text-muted-foreground">
                        ... and {importResults.imported_listings.length - 10} more
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-700 mb-2">Next Steps:</h4>
                <ol className="text-sm text-blue-600 space-y-1 list-decimal list-inside">
                  <li>Go to <strong>Homestays</strong> page and filter by "Unpublished"</li>
                  <li>Review each draft listing and add any missing details</li>
                  <li>Set prices and upload additional images if needed</li>
                  <li>Publish listings when ready to make them live</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help Section */}
        <Card>
          <CardHeader>
            <CardTitle>How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <MapPin className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="font-semibold mb-2">1. Select Locations</h4>
                <p className="text-sm text-muted-foreground">
                  Choose which Kokan region locations to search for hotels and homestays
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Download className="w-6 h-6 text-green-600" />
                </div>
                <h4 className="font-semibold mb-2">2. Import as Drafts</h4>
                <p className="text-sm text-muted-foreground">
                  DataForSEO searches Google Hotels data and creates draft listings automatically
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="font-semibold mb-2">3. Review & Publish</h4>
                <p className="text-sm text-muted-foreground">
                  Review draft listings, add missing details, and publish when ready
                </p>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-semibold text-amber-800 mb-2">Data Available from API:</h4>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-green-700 font-medium mb-1">✅ Imported Automatically:</p>
                  <ul className="text-amber-700 space-y-1 ml-4">
                    <li>• Property name</li>
                    <li>• Location coordinates (lat/lng)</li>
                    <li>• Address (via reverse geocoding)</li>
                    <li>• Images (up to 5)</li>
                    <li>• Rating &amp; review count</li>
                    <li>• Price (converted from USD to INR)</li>
                  </ul>
                </div>
                <div>
                  <p className="text-amber-700 font-medium mb-1">⚠️ Needs Manual Entry:</p>
                  <ul className="text-amber-700 space-y-1 ml-4">
                    <li>• Contact number/phone</li>
                    <li>• Website URL</li>
                    <li>• Facilities/amenities</li>
                    <li>• Check-in/check-out times</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminSEOImport;
